import React from 'react';
import { withLaunchDarkly, type LaunchDarklyFlags } from '../common/launchDarkly';
import FooterLinks from '../components/common/FooterLinks';
import ManagementHeader from '../components/common/ManagementHeader';
import Spinner from '../components/common/Spinner';
import { PreferenceRadioPanel, BillingManagementPanel } from '../components/userPreferences';
import { HeaderContext } from '../context/header-context';
import {
  signaturePreference,
  signaturePreferenceOptions,
  keepBlocksSelectedPreference,
  keepBlockSelectedOptions,
  emailDocsAsAttachmentPreferenceOptions,
  emailDocsAsAttachmentPreference,
  dateFormatPreference,
  dateFormatPreferenceOptions,
  automatedEmailsReminderPreference,
  automatedEmailsReminderPreferenceOptions,
} from '../lib/preferencePanels';
import { useShallowSelector, useDispatch } from '../lib/reduxHooks';
import { UserOrigins } from '../lib/types';
import type { IRootState } from '../store';
import * as actions from '../store/pageFrame/actions';
import type { AutoStampDateTimePreference, DateFormatType } from '../store/pageFrame/types';

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const selector = (state: IRootState) => ({
  signers: state.senderBuild.signers,
  signerGroups: state.senderBuild.signingGroups,
  settings: state.pageFrame.settings,
  settingsFetched: state.pageFrame.settingsFetched,
  contacts: state.senderBuild.recipientsModal.contacts,
});

function UserPreferences({ flags }: { flags: LaunchDarklyFlags }) {
  const dispatch = useDispatch();
  const { settings, settingsFetched } = useShallowSelector(selector);
  const headerContext = React.useContext(HeaderContext);
  const [loading, setLoading] = React.useState(true);

  const showEmailSettingsPreference = flags && flags['enable-email-settings-preference'];
  const showDateFormatPreference = flags && flags['enable-date-stamping-preferences'];

  const isStartSafeAccount = settingsFetched && settings?.account?.userOrigin === UserOrigins.DOP;
  const showAutomatedRemindersFlag = Boolean(
    flags && flags['automated-reminders'] && settingsFetched && !isStartSafeAccount
  );

  React.useEffect(() => {
    if (settingsFetched) {
      setLoading(false);
    }

    headerContext.setContent(
      <ManagementHeader resource="Preference" flags={flags} userOrigin={settings?.account.userOrigin} />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [settingsFetched]);

  if (loading) return <Spinner />;

  return (
    <div className="flex flex-col max-w-[1200px]">
      <PreferenceRadioPanel
        data={signaturePreference}
        initialSelection={settings?.user?.autoStampDateTime?.preference || 'None'}
        options={signaturePreferenceOptions}
        onSave={(val: string) => {
          dispatch(
            actions.updateUserSettings({ autoStampDateTime: { preference: val as AutoStampDateTimePreference } })
          );
        }}
      />
      {showDateFormatPreference && (
        <PreferenceRadioPanel
          data={dateFormatPreference}
          initialSelection={settings?.user.dateFormat?.preference || 'MM/DD/YYYY'}
          options={dateFormatPreferenceOptions}
          onSave={(val: string) => {
            dispatch(actions.updateUserSettings({ dateFormat: { preference: val as DateFormatType } }));
          }}
        />
      )}
      <PreferenceRadioPanel
        data={keepBlocksSelectedPreference}
        initialSelection={(settings?.user.keepBlocksSelected || false).toString()}
        options={keepBlockSelectedOptions}
        onSave={(val: string) => {
          dispatch(actions.updateUserSettings({ keepBlocksSelected: val === 'true' }));
        }}
      />
      {showEmailSettingsPreference && (
        <PreferenceRadioPanel
          dataSpec="attachmentSettings"
          data={emailDocsAsAttachmentPreference}
          initialSelection={String(
            settings?.user.emailDocsAsAttachment !== undefined ? settings?.user.emailDocsAsAttachment : true
          )}
          options={emailDocsAsAttachmentPreferenceOptions}
          onSave={(val: string) => {
            dispatch(actions.updateUserSettings({ emailDocsAsAttachment: val === 'true' }));
          }}
        />
      )}
      {showAutomatedRemindersFlag && (
        <PreferenceRadioPanel
          dataSpec={automatedEmailsReminderPreference.dataSpec}
          data={automatedEmailsReminderPreference}
          initialSelection={String(settings?.user.automatedReminders?.enabled ?? true)}
          options={automatedEmailsReminderPreferenceOptions}
          onSave={(val: string) => {
            dispatch(actions.updateUserSettings({ automatedReminders: { enabled: val === 'true' } }));
          }}
        />
      )}
      <BillingManagementPanel />
      <div className="absolute bottom-[40px] left-[100px] right-[100px]">
        <FooterLinks showSocialIcons customMarginTop="0px" />
      </div>
    </div>
  );
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default withLaunchDarkly(UserPreferences);
